<script setup lang="ts">
import { ref } from 'vue'
import { X, UserPlus, CheckCircle2, Building, Mail, User, Phone, FileText, Shield, Search, Key } from 'lucide-vue-next'
import { $dialog } from '@/plugins/dialog-alert'
import apiHelper from '@/utils/apiHelper'
import { API_URL } from '@/constants/api'
import FilterDropdown from '@/components/FilterDropdown.vue'

const ROLE_OPTIONS = [
  { value: 'RESEARCHER', label: '연구원 (RESEARCHER)' },
  { value: 'COOPERATION_PARTNER', label: '협력 파트너 (COOPERATION_PARTNER)' },
  { value: 'PROJECT_MANAGER', label: '프로젝트 관리자 (PROJECT_MANAGER)' },
]

const props = defineProps<{
  isOpen: boolean
}>()

const emit = defineEmits<{
  (e: 'close'): void
}>()

import {
  EMAIL_REGEX,
  EMAIL_MAX_LENGTH,
  NAME_MAX_LENGTH,
  ORGANIZATION_MAX_LENGTH,
  isValidEmail,
  isValidName,
  isValidOrganization,
  isValidPhoneNo,
  formatPhoneNo
} from '@/utils/validation'

const userId = ref('')
const password = ref('')
const passwordConfirm = ref('')
const userName = ref('')
const organization = ref('')
const phoneNo = ref('')
const roleId = ref('RESEARCHER')
const reason = ref('')

const isChecking = ref(false)
const isSubmitting = ref(false)
const isIdChecked = ref(false)
const checkedIdValue = ref('')

const userIdError = ref('')
const userIdSuccess = ref('')
const passwordError = ref('')
const passwordConfirmError = ref('')
const userNameError = ref('')
const orgError = ref('')
const phoneError = ref('')

function handleClose() {
  resetForm()
  emit('close')
}

function resetForm() {
  userId.value = ''
  password.value = ''
  passwordConfirm.value = ''
  userName.value = ''
  organization.value = ''
  phoneNo.value = ''
  roleId.value = 'RESEARCHER'
  reason.value = ''

  isChecking.value = false
  isSubmitting.value = false
  isIdChecked.value = false
  checkedIdValue.value = ''

  userIdError.value = ''
  userIdSuccess.value = ''
  passwordError.value = ''
  passwordConfirmError.value = ''
  userNameError.value = ''
  orgError.value = ''
  phoneError.value = ''
}

function handleUserIdInput() {
  userIdError.value = ''
  userIdSuccess.value = ''
  isIdChecked.value = false
  checkedIdValue.value = ''
}

function validatePasswordInput() {
  const val = password.value
  if (val.length > 0 && val.length < 4) {
    passwordError.value = '비밀번호는 4~30자 이내로 입력해 주세요.'
  } else if (val.length > 30) {
    passwordError.value = '비밀번호는 30자 이내로 입력해 주세요.'
  } else {
    passwordError.value = ''
  }

  if (passwordConfirm.value) {
    validatePasswordConfirmInput()
  }
}

function validatePasswordConfirmInput() {
  const val = passwordConfirm.value
  if (!val) {
    passwordConfirmError.value = ''
  } else if (password.value && val !== password.value) {
    passwordConfirmError.value = '비밀번호가 일치하지 않습니다.'
  } else {
    passwordConfirmError.value = ''
  }
}

function handleUserNameInput() {
  const val = userName.value.trim()
  if (val.length > NAME_MAX_LENGTH) {
    userNameError.value = `성명은 ${NAME_MAX_LENGTH}자 이내로 입력해 주세요.`
  } else {
    userNameError.value = ''
  }
}

function handleOrgInput() {
  const val = organization.value.trim()
  if (val.length > ORGANIZATION_MAX_LENGTH) {
    orgError.value = `소속 기관/부서는 ${ORGANIZATION_MAX_LENGTH}자 이내로 입력해 주세요.`
  } else {
    orgError.value = ''
  }
}

function handlePhoneInput(e: Event) {
  const val = (e.target as HTMLInputElement).value
  phoneNo.value = formatPhoneNo(val)
  if (phoneNo.value && !isValidPhoneNo(phoneNo.value)) {
    phoneError.value = '올바른 휴대전화 번호 형식이 아닙니다. (예: 010-1234-5678)'
  } else {
    phoneError.value = ''
  }
}

async function handleCheckId() {
  userIdError.value = ''
  userIdSuccess.value = ''
  const trimmedId = userId.value.trim()

  if (!trimmedId) {
    userIdError.value = '신청 아이디(이메일)를 입력해주세요.'
    return
  }

  if (!isValidEmail(trimmedId)) {
    userIdError.value = '올바른 이메일 형식이 아닙니다. (예: example@kriss.re.kr)'
    return
  }

  if (trimmedId.length > EMAIL_MAX_LENGTH) {
    userIdError.value = `아이디(이메일)는 ${EMAIL_MAX_LENGTH}자 이내로 입력해 주세요.`
    return
  }

  isChecking.value = true
  try {
    const res = await apiHelper.post<{ exists: boolean; available: boolean }>(API_URL.AUTH.CHECK_ID, {
      userId: trimmedId,
    })

    if (res && res.errorCode === 0 && res.data) {
      if (res.data.available) {
        isIdChecked.value = true
        checkedIdValue.value = trimmedId
        userIdSuccess.value = '사용 가능한 아이디입니다.'
      } else {
        isIdChecked.value = false
        checkedIdValue.value = ''
        userIdError.value = '이미 사용 중이거나 등록된 아이디입니다.'
      }
    } else {
      isIdChecked.value = false
      checkedIdValue.value = ''
      userIdError.value = res?.errorMsg || '중복 확인에 실패했습니다. 다른 아이디를 입력해주세요.'
    }
  } catch (e: any) {
    isIdChecked.value = false
    checkedIdValue.value = ''
    userIdError.value = '아이디 중복 확인 중 오류가 발생했습니다.'
  } finally {
    isChecking.value = false
  }
}

async function handleSubmit() {
  userIdError.value = ''
  passwordError.value = ''
  passwordConfirmError.value = ''
  userNameError.value = ''
  orgError.value = ''
  phoneError.value = ''

  let isValid = true
  const trimmedId = userId.value.trim()
  const trimmedName = userName.value.trim()
  const trimmedOrg = organization.value.trim()

  if (!trimmedId) {
    userIdError.value = '신청 아이디(이메일)를 입력해주세요.'
    isValid = false
  } else if (!isValidEmail(trimmedId)) {
    userIdError.value = '올바른 이메일 형식이 아닙니다. (예: example@kriss.re.kr)'
    isValid = false
  } else if (trimmedId.length > EMAIL_MAX_LENGTH) {
    userIdError.value = `아이디(이메일)는 ${EMAIL_MAX_LENGTH}자 이내로 입력해 주세요.`
    isValid = false
  } else if (!isIdChecked.value || checkedIdValue.value !== trimmedId) {
    userIdError.value = '아이디 중복확인을 진행해주세요.'
    isValid = false
  }

  if (!password.value) {
    passwordError.value = '비밀번호를 입력해주세요.'
    isValid = false
  } else if (password.value.length < 4) {
    passwordError.value = '비밀번호는 4~30자 이내로 입력해 주세요.'
    isValid = false
  } else if (password.value.length > 30) {
    passwordError.value = '비밀번호는 30자 이내로 입력해 주세요.'
    isValid = false
  }

  if (!passwordConfirm.value) {
    passwordConfirmError.value = '비밀번호 확인을 입력해주세요.'
    isValid = false
  } else if (password.value && password.value !== passwordConfirm.value) {
    passwordConfirmError.value = '비밀번호가 일치하지 않습니다.'
    isValid = false
  }

  if (!trimmedName) {
    userNameError.value = '성명을 입력해주세요.'
    isValid = false
  } else if (trimmedName.length > NAME_MAX_LENGTH) {
    userNameError.value = `성명은 ${NAME_MAX_LENGTH}자 이내로 입력해 주세요.`
    isValid = false
  }

  if (!trimmedOrg) {
    orgError.value = '소속 기관/부서를 입력해주세요.'
    isValid = false
  } else if (trimmedOrg.length > ORGANIZATION_MAX_LENGTH) {
    orgError.value = `소속 기관/부서는 ${ORGANIZATION_MAX_LENGTH}자 이내로 입력해 주세요.`
    isValid = false
  }

  if (phoneNo.value && !isValidPhoneNo(phoneNo.value)) {
    phoneError.value = '올바른 휴대전화 번호 형식이 아닙니다. (예: 010-1234-5678)'
    isValid = false
  }

  if (!trimmedName) {
    userNameError.value = '성명을 입력해주세요.'
    isValid = false
  } else if (trimmedName.length > NAME_MAX_LENGTH) {
    userNameError.value = `성명은 ${NAME_MAX_LENGTH}자 이내로 입력해 주세요.`
    isValid = false
  }

  if (!trimmedOrg) {
    orgError.value = '소속 기관/부서를 입력해주세요.'
    isValid = false
  } else if (trimmedOrg.length > ORGANIZATION_MAX_LENGTH) {
    orgError.value = `소속 기관/부서는 ${ORGANIZATION_MAX_LENGTH}자 이내로 입력해 주세요.`
    isValid = false
  }

  if (phoneNo.value && !isValidPhoneNo(phoneNo.value)) {
    phoneError.value = '올바른 휴대전화 번호 형식이 아닙니다. (예: 010-1234-5678)'
    isValid = false
  }

  if (!isValid) return

  isSubmitting.value = true
  try {
    const res = await apiHelper.post(API_URL.AUTH.REQUEST_ACCOUNT, {
      userId: trimmedId,
      userPw: password.value,
      userName: trimmedName,
      organization: trimmedOrg,
      phoneNo: phoneNo.value.trim(),
      roleId: roleId.value,
      reason: reason.value.trim(),
    })

    if (res && res.errorCode === 0) {
      handleClose()
      $dialog.success(
        '계정 신청 완료',
        `계정 발급 신청이 성공적으로 접수되었습니다.\n\n- 신청 아이디: ${trimmedId}\n- 성명: ${trimmedName}\n- 소속: ${trimmedOrg}\n\n시스템 관리자 승인 후 로그인 가능합니다.`
      )
    } else {
      $dialog.error('신청 실패', res?.errorMsg || '계정 신청 처리 중 오류가 발생했습니다.')
    }
  } catch (e: any) {
    $dialog.error('신청 실패', '계정 신청 중 오류가 발생했습니다.')
  } finally {
    isSubmitting.value = false
  }
}
</script>

<template>
  <Teleport to="body">
    <div v-if="isOpen" class="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 overflow-y-auto">
      <!-- Backdrop -->
      <div class="fixed inset-0 bg-slate-900/65 backdrop-blur-sm transition-opacity" />

      <!-- Modal Card -->
      <div class="relative bg-white w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden border border-slate-100 transform transition-all z-10 animate-modal-enter">
        <!-- Modal Header -->
        <div class="px-6 pt-6 pb-4 bg-gradient-to-r from-blue-900 to-indigo-900 text-white relative">
          <button @click="handleClose" class="absolute top-5 right-5 text-white/70 hover:text-white transition-colors p-1 rounded-lg hover:bg-white/10">
            <X class="w-5 h-5" />
          </button>
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center border border-white/20">
              <UserPlus class="w-5 h-5 text-blue-200" />
            </div>
            <div>
              <h2 class="text-lg font-bold text-white tracking-tight">계정 발급 신청</h2>
              <p class="text-xs text-blue-200 mt-0.5">KRISS 전자현미경 연구 데이터 플랫폼 사용 신청</p>
            </div>
          </div>
        </div>

        <!-- Form Body -->
        <form @submit.prevent="handleSubmit" class="p-6 space-y-4">
          <!-- User ID / Email with Check Duplicate Button -->
          <div class="space-y-1">
            <label class="block text-xs font-bold text-slate-700 flex items-center gap-1.5">
              <Mail class="w-3.5 h-3.5 text-blue-600" />
              신청 아이디 (이메일) <span class="text-rose-500">*</span>
            </label>
            <div class="flex items-center gap-2">
              <input v-model="userId" type="text" placeholder="example@kriss.re.kr"
                :class="[
                  'flex-1 px-3.5 py-2.5 bg-slate-50 border rounded-xl text-sm outline-none transition-all',
                  userIdError ? 'border-rose-400 focus:ring-2 focus:ring-rose-500/20' : (isIdChecked ? 'border-emerald-500 focus:ring-2 focus:ring-emerald-500/20' : 'border-slate-200 focus:border-[#6366F1]')
                ]"
                @input="handleUserIdInput" />
              <button type="button" @click="handleCheckId" :disabled="isChecking || !userId.trim()"
                class="px-3.5 py-2.5 bg-slate-800 hover:bg-slate-900 active:scale-95 disabled:bg-slate-300 text-white font-bold text-xs rounded-xl shadow transition-all shrink-0 flex items-center gap-1.5 cursor-pointer">
                <Search class="w-3.5 h-3.5" />
                {{ isChecking ? '확인 중...' : '중복확인' }}
              </button>
            </div>
            <p v-if="userIdError" class="text-xs text-rose-500 font-semibold mt-1 ml-0.5">{{ userIdError }}</p>
            <p v-if="userIdSuccess" class="text-xs text-emerald-600 font-semibold mt-1 ml-0.5 flex items-center gap-1">
              <CheckCircle2 class="w-3.5 h-3.5" />
              {{ userIdSuccess }}
            </p>
          </div>

          <!-- Password & Password Confirm Grid -->
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <!-- Password -->
            <div class="space-y-1">
              <label class="block text-xs font-bold text-slate-700 flex items-center gap-1.5">
                <Key class="w-3.5 h-3.5 text-blue-600" />
                비밀번호 <span class="text-rose-500">*</span>
              </label>
              <input v-model="password" type="password" placeholder="••••••••"
                :class="[
                  'w-full px-3.5 py-2.5 bg-slate-50 border rounded-xl text-sm outline-none transition-all',
                  passwordError ? 'border-rose-400 focus:ring-2 focus:ring-rose-500/20' : 'border-slate-200 focus:border-[#6366F1]'
                ]"
                @input="validatePasswordInput" />
              <p v-if="passwordError" class="text-xs text-rose-500 font-semibold ml-0.5">{{ passwordError }}</p>
            </div>

            <!-- Password Confirm -->
            <div class="space-y-1">
              <label class="block text-xs font-bold text-slate-700 flex items-center gap-1.5">
                <Key class="w-3.5 h-3.5 text-blue-600" />
                비밀번호 확인 <span class="text-rose-500">*</span>
              </label>
              <input v-model="passwordConfirm" type="password" placeholder="••••••••"
                :class="[
                  'w-full px-3.5 py-2.5 bg-slate-50 border rounded-xl text-sm outline-none transition-all',
                  passwordConfirmError ? 'border-rose-400 focus:ring-2 focus:ring-rose-500/20' : 'border-slate-200 focus:border-[#6366F1]'
                ]"
                @input="validatePasswordConfirmInput" />
              <p v-if="passwordConfirmError" class="text-xs text-rose-500 font-semibold ml-0.5">{{ passwordConfirmError }}</p>
            </div>
          </div>

          <!-- Name & Phone Grid -->
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <!-- Full Name -->
            <div class="space-y-1">
              <label class="block text-xs font-bold text-slate-700 flex items-center gap-1.5">
                <User class="w-3.5 h-3.5 text-blue-600" />
                성명 <span class="text-rose-500">*</span>
              </label>
              <input v-model="userName" type="text" placeholder="홍길동"
                :class="[
                  'w-full px-3.5 py-2.5 bg-slate-50 border rounded-xl text-sm outline-none transition-all',
                  userNameError ? 'border-rose-400 focus:ring-2 focus:ring-rose-500/20' : 'border-slate-200 focus:border-[#6366F1]'
                ]"
                @input="handleUserNameInput" />
              <p v-if="userNameError" class="text-xs text-rose-500 font-semibold">{{ userNameError }}</p>
            </div>

            <!-- Phone Number -->
            <div class="space-y-1">
              <label class="block text-xs font-bold text-slate-700 flex items-center gap-1.5">
                <Phone class="w-3.5 h-3.5 text-blue-600" />
                연락처 <span class="text-slate-400 font-normal">(숫자만 입력)</span>
              </label>
              <input 
                v-model="phoneNo" 
                type="text" 
                placeholder="010-0000-0000"
                :class="[
                  'w-full px-3.5 py-2.5 bg-slate-50 border rounded-xl text-sm outline-none transition-all',
                  phoneError ? 'border-rose-400 focus:ring-2 focus:ring-rose-500/20' : 'border-slate-200 focus:border-[#6366F1]'
                ]"
                @input="handlePhoneInput" 
              />
              <p v-if="phoneError" class="text-xs text-rose-500 font-semibold">{{ phoneError }}</p>
            </div>
          </div>

          <!-- Organization -->
          <div class="space-y-1">
            <label class="block text-xs font-bold text-slate-700 flex items-center gap-1.5">
              <Building class="w-3.5 h-3.5 text-blue-600" />
              소속 기관 / 부서 <span class="text-rose-500">*</span>
            </label>
            <input v-model="organization" type="text" placeholder="예: 한국표준과학연구원 데이터연구팀"
              :class="[
                'w-full px-3.5 py-2.5 bg-slate-50 border rounded-xl text-sm outline-none transition-all',
                orgError ? 'border-rose-400 focus:ring-2 focus:ring-rose-500/20' : 'border-slate-200 focus:border-[#6366F1]'
              ]"
              @input="handleOrgInput" />
            <p v-if="orgError" class="text-xs text-rose-500 font-semibold">{{ orgError }}</p>
          </div>

          <!-- Role Selection -->
          <div class="space-y-1">
            <label class="block text-xs font-bold text-slate-700 flex items-center gap-1.5">
              <Shield class="w-3.5 h-3.5 text-blue-600" />
              신청 권한 유형
            </label>
            <FilterDropdown
              v-model="roleId"
              placeholder="권한 선택"
              :options="ROLE_OPTIONS"
              :clearable="false"
              full-width
            />
          </div>

          <!-- Application Reason -->
          <div class="space-y-1">
            <label class="block text-xs font-bold text-slate-700 flex items-center gap-1.5">
              <FileText class="w-3.5 h-3.5 text-blue-600" />
              신청 사유 및 비고
            </label>
            <textarea v-model="reason" rows="3" placeholder="연구 프로젝트명 또는 계정 신청 사유를 간단히 입력해주세요."
              class="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:border-[#6366F1] transition-all resize-none" />
          </div>

          <!-- Actions -->
          <div class="pt-3 flex items-center justify-end gap-2.5">
            <button type="button" @click="handleClose"
              class="px-4 py-2.5 rounded-xl border border-slate-200 text-slate-700 text-xs font-bold hover:bg-slate-100 transition-all cursor-pointer">
              취소
            </button>
            <button type="submit" :disabled="isSubmitting"
              class="px-5 py-2.5 rounded-xl bg-[#0D1B3E] hover:bg-[#1a2d5a] disabled:opacity-50 text-white text-xs font-bold shadow-md hover:shadow-lg transition-all flex items-center gap-1.5 active:scale-95 cursor-pointer">
              <CheckCircle2 class="w-4 h-4" />
              {{ isSubmitting ? '신청 중...' : '신청 제출' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
@keyframes modalEnter {
  from {
    opacity: 0;
    transform: scale(0.95) translateY(10px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}
.animate-modal-enter {
  animation: modalEnter 0.25s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}
</style>
